import styles from './index.module.css'

import { LuPlus } from '@react-icons/all-files/lu/LuPlus'
import React, { useCallback, useMemo, useState } from 'react'

import { filterFilters } from '@disco/utils/search-page'

import { FilterDefinition, FilterState } from 'types/filter-page'

import {
  Badge,
  Button,
  DropdownMenu,
  Flex,
  SearchTextField,
} from 'components/ds'
import HighlightedText from 'components/ui/highlighted-text'

type Props = {
  definitions: FilterDefinition[]
  currentFilterStates: FilterState[]
  onAddFilter: (_filter: FilterDefinition) => void
}

const highlightColors = {
  background: 'var(--disco-yellow)',
}

const AddFilter = ({
  definitions,
  currentFilterStates,
  onAddFilter,
}: Props) => {
  const [filterKeywords, setFilterKeywords] = useState('')

  const filters = useMemo(
    () =>
      filterFilters(filterKeywords, definitions)
        .filter(f => !f.fixed)
        // filter out filters that have no filter options to hide from dropdown
        .filter(f => !f.options || f.options.length > 0),
    [filterKeywords],
  )

  const isFilterSelected = useCallback(
    (filter: FilterDefinition) => {
      const isRootFilterSelected = currentFilterStates.some(
        state => state.definition.label === filter.label,
      )
      const isNestedFilterSelected = currentFilterStates.some(state =>
        state.definition.options?.some(option => option.label === filter.label),
      )
      return isRootFilterSelected || isNestedFilterSelected
    },
    [currentFilterStates],
  )

  return (
    <div className={styles.trigger}>
      <DropdownMenu.Root modal={false}>
        <DropdownMenu.Trigger>
          <Button highContrast color="gray" variant="soft">
            <LuPlus />
            Filter
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content variant="soft">
          <div
            onKeyDown={e => {
              e.stopPropagation()
            }}
            onKeyUp={e => {
              e.stopPropagation()
            }}
          >
            <SearchTextField
              mb="2"
              placeholder="Search filters"
              onChange={e => setFilterKeywords(e.target.value)}
              value={filterKeywords}
            />
          </div>
          {filters.map(filter =>
            filter.options ? (
              <DropdownMenu.Sub key={filter.label}>
                <DropdownMenu.SubTrigger
                  onPointerEnter={e => {
                    // Force immediate open on hover
                    const target = e.currentTarget as HTMLElement
                    target.click()
                  }}
                >
                  <HighlightedText
                    text={filter.label}
                    highlights={[filterKeywords]}
                    customColors={highlightColors}
                  />
                </DropdownMenu.SubTrigger>
                <DropdownMenu.SubContent>
                  {filter.options.map(option => (
                    <DropdownMenu.Item
                      key={option.field}
                      onSelect={() => onAddFilter(option)}
                      disabled={isFilterSelected(option)}
                    >
                      <HighlightedText
                        text={option.label}
                        highlights={[filterKeywords]}
                        customColors={highlightColors}
                      />
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.SubContent>
              </DropdownMenu.Sub>
            ) : (
              <DropdownMenu.Item
                key={filter.label}
                onSelect={() => onAddFilter(filter)}
                disabled={isFilterSelected(filter)}
              >
                <Flex align="center" gap="2">
                  <HighlightedText
                    text={filter.label}
                    highlights={[filterKeywords]}
                    customColors={highlightColors}
                  />
                  {filter.type === 'playlist_count' && (
                    <Badge color="blue" size="1">
                      NEW
                    </Badge>
                  )}
                </Flex>
              </DropdownMenu.Item>
            ),
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
      <span className={styles.dot} />
    </div>
  )
}

export default AddFilter
